// Ported from authentication/forgot-password/page/forgot-password.page.tpl.html.
// Markup and classes verbatim: `chrome-extension-forgot-password-page popup-styles`,
// `woo-form page-content`, `title`, `woo-field email`, `error`, `text-content`.
//
// ⚠️ `woo-form page-content` is a DIV, not a `<form>`. Upstream uses `ng-form`, an attribute
// that AngularJS puts on a div — it never renders a form element. A real `<form>` brings the
// browser's own submit behaviour, its default margin, and different intrinsic sizing: the
// ported login screen measured 7px wider than production at 200px until this changed. Enter
// handling is wired per field instead, exactly where upstream's `ng-keydown` sits.
import { useState, type ReactElement } from 'react'
import { AuthNav } from './AuthNav.tsx'
import { LoadingButton } from './LoadingButton.tsx'
import { PageHost } from './PageHost.tsx'
import { useEmailValidProbe } from './ssoRequiredProbe.ts'

export interface ForgotPasswordPageProps {
  onRequestReset: (email: string) => Promise<void>
  /**
   * The extension popup's `email-validator` (`isValidEmail`), with no debounce — the popup's
   * forgot-password field has no `ng-model-options`. See `useEmailValidProbe`.
   */
  checkEmailValid?: (email: string) => Promise<boolean>
  admin?: boolean
}

export function ForgotPasswordPage({ onRequestReset, checkEmailValid, admin = false }: ForgotPasswordPageProps): ReactElement {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [invalid, setInvalid] = useState(false)
  const [failed, setFailed] = useState(false)
  const [completed, setCompleted] = useState(false)
  const validity = useEmailValidProbe(checkEmailValid, 0)

  const request = async (): Promise<void> => {
    setSubmitted(true)
    const bad = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || validity.ssoRequired
    setInvalid(bad)
    setFailed(false)
    if (bad) return
    try {
      await onRequestReset(email)
      // Upstream shows the same confirmation whether or not the address exists — a
      // deliberate account-enumeration boundary, preserved.
      setCompleted(true)
    } catch {
      setFailed(true)
    }
  }

  // No `ng-keydown` on this template: upstream's forgot-password form does NOT submit on
  // Enter, and the <form> the first port used did. Left as upstream has it.

  return (
    <PageHost name="chrome-extension-forgot-password-page" className="popup-styles" hostClassName="non-extended-ui">
      <AuthNav admin={admin} />

      {completed ? (
        <div className="page-content">
          <div className="text-content">
            Password reset email sent, check your mailbox to complete the process
          </div>
        </div>
      ) : (
        <div className="woo-form page-content">
          <h1 className="title">Reset password</h1>
          <input
            className="woo-field email"
            type="email"
            placeholder="Email"
            name="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value)
              validity.onChange(event.target.value)
            }}
            required
          />
          {submitted && (invalid || failed) ? (
            <div className="error">
              {invalid ? <div>Please enter an email address</div> : null}
              {failed ? <div>Something went wrong, please try again</div> : null}
            </div>
          ) : null}
          <LoadingButton text="Send" classOverride="form-btn" onClick={request} />
        </div>
      )}
    </PageHost>
  )
}
